import { useEffect, useState } from "react";
import Title from "../../../components/Admin/Title";
import useIsAdmin from "../../../hooks/useIsAdmin";
import ErrorPage from "../../ErrorPage";
import CircuitForm from "../../../components/Admin/CircuitForm";
import Button from "../../../components/Button";
import axios from "axios";
import { Col, Form, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { useUser } from "../../../components/UserContext";
import { toast } from "react-toastify";

interface Circuit {
  id: number;
  name: string;
  layout: string;
  turns: number;
  length: number;
  lat: number;
  lon: number;
  isRallyEvent: number;
}

interface RallyCircuit {
  id: number;
  name: string;
  stages: Stage[];
}

interface Stage {
  id: number;
  leg: number;
  stage: number;
  weather: string;
}

const AssignRallyCircuits = () => {
  const { access } = useIsAdmin();
  const { user } = useUser() as any;
  document.title = "LVF1 - Assign Rally Circuits";
  const [stages, setStages] = useState(0);
  const [dataForm, setDataForm] = useState({
    name: "",
    layout: "",
    turns: "",
    length: "",
    latitude: "",
    longitude: "",
    id: "",
    stage: false,
    rally: "",
  });
  const [circuits, setCircuits] = useState<Circuit[]>([]);
  const [rallyCircuits, setRallyCircuits] = useState<RallyCircuit[]>([]);
  const [stageData, setStageData] = useState<
    Array<{ circuitId: string; leg: string; stage: number; weather: string }>
  >([]);
  const [uploading, setUploading] = useState(false);
  const [validated, setValidated] = useState(false);
  const [updated, setUpdate] = useState(false);

  useEffect(() => {
    axios
      .get(
        `${
          process.env.NODE_ENV === "development"
            ? "http://localhost:5500"
            : "https://ligavirtualf1.es:5500"
        }/api/circuit/circuits`,
      )
      .then(({ data }) => {
        setCircuits(data);
      })
      .catch(({ response }) => {
        console.log(response);
        throw new Error(response);
      });
  }, [updated]);

  useEffect(() => {
    axios
      .get(
        `${
          process.env.NODE_ENV === "development"
            ? "http://localhost:5500"
            : "https://ligavirtualf1.es:5500"
        }/api/circuit/rallyCircuits`,
      )
      .then(({ data }) => {
        setRallyCircuits(data);
      })
      .catch(({ response }) => {
        console.log(response);
        throw new Error(response);
      });
  }, [updated]);

  useEffect(() => {
    setStageData((prev) =>
      Array.from(
        { length: stages },
        (_, i) => prev[i] || { circuitId: "", leg: "", stage: i + 1 },
      ),
    );
  }, [stages]);

  const updateDataForm = ({ target }: { target: any }) => {
    const { value } = target;
    const rallyCircuit = rallyCircuits.find(
      (rally) => rally.id == parseInt(value),
    );
    if (rallyCircuit != null) {
      setStages(rallyCircuit.stages.length);
      setStageData(
        rallyCircuit.stages.map((stage) => ({
          circuitId: stage.id.toString(),
          leg: stage.leg.toString(),
          stage: stage.stage,
          weather: stage.weather || "",
        })),
      );
      setDataForm({
        ...dataForm,
        id: value,
        name: rallyCircuit.name,
      });
    }
  };

  const submitForm = () => {
    const token = window.localStorage.getItem("userLogged");
    if (token == null) return;
    const formData = new FormData();
    formData.append("rallyId", dataForm.id);
    formData.append("stageData", JSON.stringify(stageData));
    formData.append("user", user);
    formData.append("token", JSON.parse(token).token);
    setUploading(true);
    axios
      .post(
        `${
          process.env.NODE_ENV === "development"
            ? "http://localhost:5500"
            : "https://ligavirtualf1.es:5500"
        }/api/admin/assignRallyCircuits`,
        formData,
      )
      .then(() => {
        toast.success("Rally circuits assigned successfully");
        setUploading(false);
        setValidated(false);
        setUpdate(!updated);
      })
      .catch(({ response }) => {
        toast.error("Error assigning rally circuits");
        setUploading(false);
        setValidated(false);
        throw new Error(response);
      });
  };

  if (!access) return <ErrorPage />;
  return (
    <div className="full-height padding-md app-container flex-center-top page-transition">
      <div className="form-container-md">
        <Title text="Assign Rally Circuits" />
        <small className="mandatory-fields">(*) Required fields</small>
        <Row className="mb-3">
          <Form.Group className="form-group" as={Col} md="9">
            <Form.Label>Rally (*)</Form.Label>
            <Form.Select
              value={dataForm.id}
              name="circuit"
              required
              onChange={(e) => updateDataForm(e)}
            >
              <option value="" disabled>
                None
              </option>
              {rallyCircuits.map((circuit) => {
                return (
                  <option key={circuit.id} value={circuit.id}>
                    {circuit.name}
                  </option>
                );
              })}
            </Form.Select>
          </Form.Group>
          <Form.Group className="form-group" as={Col} md="3">
            <Form.Label>No. Stages (*)</Form.Label>
            <Form.Control
              name="stages"
              type="number"
              required
              onChange={(e) => setStages(parseInt(e.target.value))}
              value={stages}
            />
            <Form.Control.Feedback type="invalid">
              Field required
            </Form.Control.Feedback>
          </Form.Group>
        </Row>
        {Array.from({ length: stages }, (_, index) => (
          <Row key={index} className="mb-3">
            <Form.Group className="form-group" as={Col} md="2">
              <Form.Label>Stage</Form.Label>
              <Form.Control
                type="number"
                value={stageData[index]?.stage || index + 1}
                readOnly
              />
            </Form.Group>
            <Form.Group className="form-group" as={Col} md="5">
              <Form.Label>Stage {index + 1} Circuit</Form.Label>
              <Form.Select
                value={stageData[index]?.circuitId || ""}
                onChange={(e) =>
                  setStageData((prev) => {
                    const newArr = [...prev];
                    newArr[index] = {
                      ...newArr[index],
                      circuitId: e.target.value,
                    };
                    return newArr;
                  })
                }
              >
                <option value="" disabled>
                  None
                </option>
                {circuits.map((circuit) => (
                  <option key={circuit.id} value={circuit.id}>
                    {circuit.name}{" "}
                    {circuit.layout != null && `(${circuit.layout})`}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="form-group" as={Col} md="2">
              <Form.Label>Stage {index + 1} Leg</Form.Label>
              <Form.Control
                type="number"
                placeholder="Enter stage leg"
                value={stageData[index]?.leg || ""}
                onChange={(e) =>
                  setStageData((prev) => {
                    const newArr = [...prev];
                    newArr[index] = {
                      ...newArr[index],
                      leg: e.target.value,
                    };
                    return newArr;
                  })
                }
              />
            </Form.Group>
            <Form.Group className="form-group" as={Col} md="3">
              <Form.Label>Weather Stage {index + 1}</Form.Label>
              <Form.Control
                type="string"
                placeholder="Enter stage weather"
                value={stageData[index]?.weather || ""}
                onChange={(e) =>
                  setStageData((prev) => {
                    const newArr = [...prev];
                    newArr[index] = {
                      ...newArr[index],
                      weather: e.target.value,
                    };
                    return newArr;
                  })
                }
              />
            </Form.Group>
          </Row>
        ))}
        <div className="text-center">
          {uploading ? (
            <FontAwesomeIcon icon={faSpinner} className="spinner-button" />
          ) : (
            <Button text={"Assign Rally Circuits"} onClick={submitForm} />
          )}
        </div>
      </div>
    </div>
  );
};

export default AssignRallyCircuits;
